import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import { Card } from 'semantic-ui-react';
import moment from 'moment';
import history from '../../history';
import { showInquiry, deleteInquiry, updateInquiry, confirmInquiry, rejectInquiry, markPaidInquiry, assignPartner } from '../../api/inquiryApi';
import { getPackageBookingDetails } from '../../api/packageBookingApi';
import { getAllPartners } from '../../api/partnerApi';
import { formatMoneyLegacy } from '../../utils/currency';
import { BASE_URL } from '../../constants';
import { PaymentMethodModal } from '../payments';
import AdminRecordOfflinePaymentModal from './AdminRecordOfflinePaymentModal';
import CreatorBadge from './CreatorBadge';
import { AdminPhaseBanner, PrivateInquiryBadge } from './AdminStatusBadge';
import { simplePrivateStatus } from '../../utils/adminBookingStatus';
import '../../styles/admin-manage.scss';

class InquiryDetails extends Component {
  state = {
    inquiry: null,
    packageBooking: {},
    fetchError: false,
    updating: false,
    sendingLink: false,
    editingCustomer: null,
    assignVendorOpen: false,
    assignVendorForm: { vendor_id: '' },
    partners: [],
    showPaymentModal: false,
    paymentModalData: null,
    offlinePaymentOpen: false,
    recordingOffline: false
  };

  componentDidMount() {
    this.fetchDetails();
  }

  fetchDetails = () => {
    const idx = this.props.match.params.idx;
    this.setState({ fetchError: false });
    showInquiry(idx)
      .then((response) => {
        const raw = response.data || {};
        const inquiry = raw.inquiry !== undefined ? raw.inquiry : raw;
        this.setState({ inquiry: inquiry || {}, fetchError: false });
        const pb = inquiry?.package_booking;
        if (pb?.idx) {
          getPackageBookingDetails(pb.idx)
            .then((r) => {
              const pbRaw = r.data || {};
              this.setState({ packageBooking: pbRaw.package_booking !== undefined ? pbRaw.package_booking : pbRaw });
            })
            .catch(() => {});
        }
      })
      .catch(() => this.setState({ inquiry: null, fetchError: true }));
  };

  destroy = (idx) => {
    swal({ title: 'Are you sure?', text: 'This inquiry will be deleted.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (ok) {
          deleteInquiry(idx)
            .then(() => { swal('Deleted', { icon: 'success' }); history.push('/admin/manage_private_tours'); })
            .catch(() => swal('Delete failed', { icon: 'error' }));
        }
      });
  };

  reject = (idx) => {
    this.setState({ updating: true });
    rejectInquiry(idx)
      .then(() => { swal('Rejected', 'Inquiry has been rejected.', 'success'); this.fetchDetails(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Rejection failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  confirmBooking = () => {
    const { inquiry } = this.state;
    this.setState({ updating: true });
    const total = inquiry.total_amount || 0;
    const values = {
      start_date: new Date(),
      end_date: new Date(),
      amount: total,
      token_amount: 0,
    };
    confirmInquiry(inquiry.idx, values)
      .then(() => { swal('Confirmed!', 'Booking confirmed. Payment link is now available.', 'success'); this.fetchDetails(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Confirmation failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  copyPaymentLink = () => {
    const { inquiry, packageBooking } = this.state;
    if (!packageBooking.idx) {
      swal('Info', 'Please confirm the booking first.', 'info');
      return;
    }
    const url = `${window.location.origin}/package_booking/${packageBooking.idx}`;
    const amount = packageBooking.amount || inquiry.total_amount;
    const cc = packageBooking?.currency_code || packageBooking?.package?.currency_code || inquiry?.package_booking?.currency_code || inquiry?.package?.currency_code || 'NPR';
    const amountStr = amount != null ? ` ${formatMoneyLegacy(Number(amount), cc)}` : '';
    navigator.clipboard.writeText(url).then(
      () => swal('Copied', `Payment link copied. Send this to ${inquiry.first_name || 'the customer'} to collect${amountStr}.`, 'success'),
      () => swal('Copy failed', `Link: ${url}`, 'info')
    );
  };

  sendPaymentLink = () => {
    const { packageBooking } = this.state;
    if (!packageBooking.idx) {
      swal('Info', 'Please confirm the booking first.', 'info');
      return;
    }
    this.setState({ sendingLink: true });
    setTimeout(() => {
      swal('Sent', 'Payment link sent to customer email and phone.', 'success');
      this.setState({ sendingLink: false });
    }, 1000);
  };

  openAdminPay = () => {
    const { inquiry, packageBooking } = this.state;
    if (!packageBooking?.idx) {
      swal('Info', 'Please confirm the booking first.', 'info');
      return;
    }
    const cc = packageBooking?.currency_code || packageBooking?.package?.currency_code || 'NPR';
    const amount = packageBooking.amount || inquiry.total_amount || 0;
    this.setState({
      showPaymentModal: true,
      paymentModalData: {
        productIdentity: packageBooking.idx,
        productName: 'PACKAGE',
        amount: Number(amount),
        currencyCode: cc,
        productUrl: `${BASE_URL || window.location.origin}/admin/inquiries/${inquiry.idx}`,
        customerName: `${inquiry.first_name || ''} ${inquiry.last_name || ''}`.trim(),
        customerEmail: inquiry.email_address,
        customerPhone: inquiry.phone,
      }
    });
  };

  openOfflinePayment = () => {
    this.setState({ offlinePaymentOpen: true });
  };

  closeOfflinePayment = () => {
    this.setState({ offlinePaymentOpen: false, recordingOffline: false });
  };

  submitOfflinePayment = (offlinePayment) => {
    const { inquiry, packageBooking } = this.state;
    if (!inquiry?.idx) return;
    this.setState({ recordingOffline: true, updating: true });
    markPaidInquiry(inquiry.idx, offlinePayment)
      .then((res) => {
        swal('Recorded', 'Offline payment recorded. Booking marked as paid.', 'success');
        const data = res.data || {};
        const rawInquiry = data.inquiry !== undefined ? data.inquiry : data;
        const nextInquiry = rawInquiry && typeof rawInquiry === 'object'
          ? { ...rawInquiry, status: data.status || rawInquiry.status || 'verified' }
          : { ...inquiry, status: 'verified' };
        this.setState({
          inquiry: nextInquiry,
          packageBooking: packageBooking?.idx ? { ...packageBooking, status: 'verified' } : packageBooking,
          offlinePaymentOpen: false
        }, () => this.fetchDetails());
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Mark paid failed', 'error'))
      .finally(() => this.setState({ recordingOffline: false, updating: false }));
  };

  markPaid = () => {
    this.openOfflinePayment();
  };

  // Customer Edit Modal
  openEditCustomer = () => {
    const { inquiry } = this.state;
    this.setState({
      editingCustomer: {
        first_name: inquiry.first_name || '',
        last_name: inquiry.last_name || '',
        email_address: inquiry.email_address || '',
        phone: inquiry.phone || '',
        number_of_adult: inquiry.number_of_adult || 1,
        number_of_child: inquiry.number_of_child || 0,
        query: inquiry.query || ''
      }
    });
  };

  closeEditCustomer = () => {
    this.setState({ editingCustomer: null });
  };

  saveEditCustomer = () => {
    const { inquiry, editingCustomer } = this.state;
    if (!editingCustomer) return;
    this.setState({ updating: true });
    updateInquiry(inquiry.idx, editingCustomer)
      .then(() => { 
        swal('Updated', 'Customer details saved.', 'success'); 
        this.closeEditCustomer(); 
        this.fetchDetails(); 
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Update failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  openAssignVendor = () => {
    const pkg = this.state.packageBooking?.package;
    getAllPartners({ q: { status_eq: 'approved', for_assignment_type: 'PartnerService' } })
      .then((r) => {
        const list = (r.data && (Array.isArray(r.data) ? r.data : r.data.partners || r.data.vendors)) || [];
        this.setState({
          assignVendorOpen: true,
          partners: list,
          assignVendorForm: { vendor_id: (pkg && pkg.vendor_id) || '' }
        });
      })
      .catch(() => this.setState({ assignVendorOpen: true, partners: [] }));
  };

  closeAssignVendor = () => this.setState({ assignVendorOpen: false });

  assignVendorSubmit = () => {
    const { inquiry, assignVendorForm } = this.state;
    if (!inquiry?.idx || !assignVendorForm.vendor_id) {
      swal('Select a vendor', 'Vendor is required.', 'info');
      return;
    }
    this.setState({ updating: true });
    // Create VendorService via inquiry assign_vendor so vendor sees the order in their dashboard
    const payload = {
      vendor_services_attributes: [JSON.stringify({ vendor_id: assignVendorForm.vendor_id })]
    };
    assignPartner(inquiry.idx, payload)
      .then(() => {
        swal('Assigned', 'Vendor assigned. They will see this tour in My Private Tours.', 'success');
        this.closeAssignVendor();
        this.fetchDetails();
        if (inquiry.package_booking && inquiry.package_booking.idx) {
          getPackageBookingDetails(inquiry.package_booking.idx).then((r) => this.setState({ packageBooking: r.data || {} })).catch(() => {});
        }
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Assign failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  render() {
    const { inquiry, packageBooking, fetchError, updating, sendingLink, editingCustomer } = this.state;

    if (fetchError) {
      return (
        <div className="container p-4">
          <div className="alert alert-warning">Inquiry not found or it has been deleted.</div>
          <Link to="/admin/manage_private_tours" className="btn btn-outline-primary">Back to Private Tours</Link>
        </div>
      );
    }

    if (!inquiry) return <div className="container p-4">Loading...</div>;

    const travellers = inquiry.travellers || [];
    const totalPeople = (inquiry.number_of_adult || 0) + (inquiry.number_of_child || 0);
    const phase = simplePrivateStatus(inquiry.status);
    const isConfirmed = inquiry.status === 'processing' || inquiry.status === 'verified';
    const isPaid = inquiry.status === 'verified';
    const isPending = inquiry.status === 'pending';
    const dueAmount = packageBooking.amount || inquiry.total_amount || 0;
    const cc = packageBooking?.currency_code || packageBooking?.package?.currency_code || inquiry?.estimated_currency_code || inquiry?.package?.currency_code || 'USD';
    const isJourneyRequest = inquiry.private_journey_request || inquiry.request_type === 'private_journey' || !!inquiry.journey_slug;

    // Get package idx for edit link - serializer now returns package_idx explicitly
    const packageIdx = inquiry.package_idx || inquiry.package?.idx || inquiry.package?.id || inquiry.package_id || null;

    return (
      <div className="container">
        <Card fluid>
          <Card.Content>
            {/* Header */}
            <div className="d-flex justify-content-between flex-wrap">
              <h3 className="title">
                {inquiry.display_name || inquiry.package_name || 'Private Tour'}
                {isJourneyRequest && <span className="badge badge-info ml-2">CMS Journey</span>}
                <CreatorBadge type={inquiry.creator_type} label={inquiry.creator_label} />
              </h3>
              <div>
                {packageIdx && (
                  <>
                    <Link
                      to={{ pathname: `/admin/package_details/${packageIdx}` }}
                      className="btn btn-outline-secondary mr-2"
                    >
                      View package
                    </Link>
                    <span className="text-muted small mr-2 align-self-center" title="Edit package is not available once anyone has inquired or booked">Edit package locked</span>
                  </>
                )}
                <button className="btn btn-outline-danger" onClick={() => this.destroy(inquiry.idx)} disabled={updating}>
                  Delete
                </button>
              </div>
            </div>

            <Link to="/admin/manage_private_tours" className="btn btn-outline-secondary btn-sm mt-2 mb-3">← Back to Private Tours</Link>
            <AdminPhaseBanner phase={phase} />
            {phase.key === 'waiting' && (
              <div className="alert alert-warning py-2 mb-3">Review the request, send a quote, or collect payment.</div>
            )}
            {isJourneyRequest && (
              <div className="mb-3 p-3 bg-light rounded">
                <h6 className="mb-2">Journey request</h6>
                <div className="list-view small">
                  <div className="list"><span className="label">Journey</span><span className="value">{inquiry.journey_title || inquiry.journey_slug || '—'}</span></div>
                  <div className="list"><span className="label">Slug</span><span className="value">{inquiry.journey_slug || '—'}</span></div>
                  <div className="list"><span className="label">Route</span><span className="value">{inquiry.journey_from_to_display || inquiry.package_from_to_display || '—'}</span></div>
                  <div className="list"><span className="label">Guide language</span><span className="value">{inquiry.preferred_language || '—'}</span></div>
                  {inquiry.special_requirements && (
                    <div className="list"><span className="label">Special requirements</span><span className="value">{inquiry.special_requirements}</span></div>
                  )}
                </div>
                {!packageIdx && isPending && (
                  <p className="text-muted small mb-0 mt-2">
                    Confirming will auto-create a platform package from CMS journey defaults and send the payment link.
                  </p>
                )}
              </div>
            )}

            {/* Vendor (partner) - assign once confirmed/paid so the vendor can run the tour */}
            {packageIdx && (
              <div className="mb-3">
                <strong>Vendor (partner)</strong>
                {packageBooking.package && packageBooking.package.vendor_id ? (
                  <>
                    <span className="ml-2">Assigned to {packageBooking.package.vendor_company_name || `Vendor #${packageBooking.package.vendor_id}`}</span>
                    <p className="text-muted small mt-1 mb-0">This tour belongs to this vendor and cannot be reassigned.</p>
                  </>
                ) : (
                  <>
                    <span className="ml-2 text-muted">Not assigned</span>
                    <button type="button" className="btn btn-outline-primary btn-sm ml-2" onClick={this.openAssignVendor} disabled={!!updating || !isPaid}>
                      Assign vendor
                    </button>
                    <p className="text-muted small mt-1 mb-0">{isPaid ? 'Assign a vendor so they can run this private tour.' : 'Assign vendor is available only after the customer has paid (Mark paid).'}</p>
                  </>
                )}
              </div>
            )}

            {/* Tour Info */}
            <div className="list-view mt-3">
              <div className="list"><span className="label">From → To</span><span className="value">{inquiry.journey_from_to_display || inquiry.package_from_to_display || '—'}</span></div>
              <div className="list"><span className="label">Preferred Date</span><span className="value">{inquiry.preferred_date ? moment(inquiry.preferred_date, 'YYYY-MM-DD').format('D MMM YYYY') : '—'}</span></div>
              <div className="list"><span className="label">{isJourneyRequest && !isConfirmed ? 'Est. Total' : 'Total Amount'}</span><span className="value">{inquiry.total_amount != null && inquiry.total_amount > 0 ? formatMoneyLegacy(inquiry.total_amount, cc) : '—'}</span></div>
              {isConfirmed && (
                <div className="list"><span className="label">Due Amount</span><span className="value">{formatMoneyLegacy(dueAmount, cc)}</span></div>
              )}
              <div className="list"><span className="label">Number of People</span><span className="value">{totalPeople}</span></div>
              <div className="list"><span className="label">Status</span><span className="value"><PrivateInquiryBadge status={inquiry.status} /></span></div>
            </div>

            {/* Pricing breakdown (vendor quote, what we charge, margin) — only when package has pricing */}
            {(() => {
              const pkg = packageBooking?.package || inquiry?.package;
              const hasPricing = pkg && (pkg.vendor_total_price != null || pkg.final_price != null);
              if (!hasPricing) return null;
              return (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="mb-2">Pricing breakdown</h6>
                  <div className="list-view small">
                    {pkg.vendor_total_price != null && (
                      <div className="list"><span className="label">Vendor quote (base)</span><span className="value">{formatMoneyLegacy(pkg.vendor_total_price, cc)}</span></div>
                    )}
                    {pkg.markup_percentage != null && pkg.markup_percentage !== '' && (
                      <div className="list"><span className="label">Markup %</span><span className="value">{Number(pkg.markup_percentage)}%</span></div>
                    )}
                    <div className="list"><span className="label">What we charge customer</span><span className="value">{formatMoneyLegacy(dueAmount, cc)}</span></div>
                    {pkg.platform_margin != null && (
                      <div className="list"><span className="label">Platform margin</span><span className="value">{formatMoneyLegacy(pkg.platform_margin, cc)}</span></div>
                    )}
                    {pkg.vendor_payout != null && (
                      <div className="list"><span className="label">Vendor payout</span><span className="value">{formatMoneyLegacy(pkg.vendor_payout, cc)}</span></div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Customer Section */}
            <div className="mt-4">
              <h5 className="mb-2">Customer</h5>
              <p className="text-muted small">
                {isPaid
                  ? <>Customer has paid. Assign a vendor above to run this tour.</>
                  : isConfirmed
                    ? <>Due: {formatMoneyLegacy(dueAmount, cc)}. Use <strong>Pay now</strong>, <strong>Copy payment link</strong>, or <strong>Record cash/bank</strong> when they pay.</>
                    : <>Review customer details. <strong>Confirm</strong> to proceed or <strong>Reject</strong> to decline.</>
                }
              </p>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone / WhatsApp</th>
                      <th>#</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{inquiry.first_name} {inquiry.last_name}</td>
                      <td>{inquiry.email_address || '—'}</td>
                      <td>{inquiry.phone || '—'}</td>
                      <td>{totalPeople}</td>
                      <td>{formatMoneyLegacy(dueAmount, cc)}</td>
                      <td><PrivateInquiryBadge status={inquiry.status} /></td>
                      <td>
                        <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={this.openEditCustomer} disabled={updating}>
                          Edit
                        </button>
                        {isPending && (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-primary mr-1" onClick={this.confirmBooking} disabled={updating}>
                              Confirm
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger mr-1" onClick={() => this.reject(inquiry.idx)} disabled={updating}>
                              Reject
                            </button>
                          </>
                        )}
                        {isConfirmed && !isPaid && (
                          <>
                            <button type="button" className="btn btn-sm btn-primary mr-1" onClick={this.openAdminPay} disabled={updating}>
                              Pay now
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-primary mr-1" onClick={this.copyPaymentLink}>
                              Copy Payment Link
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-info mr-1" onClick={this.sendPaymentLink} disabled={sendingLink}>
                              {sendingLink ? 'Sending…' : 'Send Payment Link'}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-success mr-1" onClick={this.openOfflinePayment} disabled={updating}>
                              Record cash/bank
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Travellers - always show when we have at least one person; fallback to primary contact if no travellers array */}
            {(travellers.length > 0 || totalPeople >= 1) && (
              <div className="mt-4">
                <h5 className="mb-2">Travellers</h5>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Nationality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {travellers.length > 0
                        ? travellers.slice(0, totalPeople).map((t, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{t.name || '—'}</td>
                              <td>{t.nationality || '—'}</td>
                            </tr>
                          ))
                        : (
                            <tr>
                              <td>1</td>
                              <td>{inquiry.head_traveller_name || [inquiry.first_name, inquiry.last_name].filter(Boolean).join(' ').trim() || '—'}</td>
                              <td>—</td>
                            </tr>
                          )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Special Request */}
            {inquiry.query && (
              <div className="mt-3">
                <strong>Special Request</strong>
                <p className="text-muted">{inquiry.query}</p>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Edit Customer Modal - Same as Shared Rentals */}
        {editingCustomer && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Customer</h5>
                  <button type="button" className="close" onClick={this.closeEditCustomer}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>First Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingCustomer.first_name} 
                      onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, first_name: e.target.value } })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingCustomer.last_name} 
                      onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, last_name: e.target.value } })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      value={editingCustomer.email_address} 
                      onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, email_address: e.target.value } })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone / WhatsApp</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingCustomer.phone} 
                      onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, phone: e.target.value } })} 
                      placeholder="e.g. +9779812345678"
                    />
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>Number of Adults</label>
                        <input 
                          type="number" 
                          min={1}
                          className="form-control" 
                          value={editingCustomer.number_of_adult} 
                          onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, number_of_adult: parseInt(e.target.value, 10) || 1 } })} 
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Number of Children</label>
                        <input 
                          type="number" 
                          min={0}
                          className="form-control" 
                          value={editingCustomer.number_of_child} 
                          onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, number_of_child: parseInt(e.target.value, 10) || 0 } })} 
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Special Request</label>
                    <textarea 
                      className="form-control" 
                      rows={2}
                      value={editingCustomer.query} 
                      onChange={(e) => this.setState({ editingCustomer: { ...editingCustomer, query: e.target.value } })} 
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={this.closeEditCustomer}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={this.saveEditCustomer} disabled={updating}>
                    {updating ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign vendor modal */}
        {this.state.assignVendorOpen && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign vendor</h5>
                  <button type="button" className="close" onClick={this.closeAssignVendor}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small">Assign a partner to run this private tour. They will see it under My Private Tours.</p>
                  <div className="form-group">
                    <label>Vendor (partner)</label>
                    <select
                      className="form-control"
                      value={this.state.assignVendorForm.vendor_id}
                      onChange={(e) => this.setState({ assignVendorForm: { ...this.state.assignVendorForm, vendor_id: e.target.value } })}
                    >
                      <option value="">Select vendor</option>
                      {/* Use p.id (integer) so backend stores VendorService.vendor_id = vendor.id for dashboard visibility */}
                      {this.state.partners.map((p) => (
                        <option key={p.id} value={p.id}>{p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Vendor ${p.id}`}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={this.closeAssignVendor}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={this.assignVendorSubmit} disabled={!!updating || !this.state.assignVendorForm.vendor_id}>
                    Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <PaymentMethodModal
          open={this.state.showPaymentModal}
          onClose={() => this.setState({ showPaymentModal: false, paymentModalData: null })}
          paymentParams={this.state.paymentModalData}
          amount={this.state.paymentModalData?.amount}
          onPaymentInitiate={() => this.fetchDetails()}
        />
        <AdminRecordOfflinePaymentModal
          open={this.state.offlinePaymentOpen}
          onClose={this.closeOfflinePayment}
          onSubmit={this.submitOfflinePayment}
          title="Record offline payment (private tour)"
          amount={dueAmount}
          currencyCode={cc}
          submitting={this.state.recordingOffline}
        />
      </div>
    );
  }
}

export default InquiryDetails;
